import React, {useEffect, useState} from 'react';
import styles from "./CreateContractPopUp.module.css";
import Modal from "react-modal";
import {contractableClientsAPI, createContractAPI} from "../../../services/admin";
import {CreateContractRequest, GetClientsResponseData} from "../../../types/admin";
import Calendar from '../../../components/common/calendar/Calendar';

interface CreateContractPopUpProps {
    isOpen: boolean;
    onClose: () => void;
}
interface InternalFormData {
    clientId: number;
    period: number;
    startedAt: Date;
    expireAt: Date;
    payday: number;
    unitCost: number;
    workerAmount: number;
}
//계약 추가 팝업
const CreateContractPopUp: React.FC<CreateContractPopUpProps> = ({ isOpen, onClose }) => {
    const [clients, setClients] = useState<GetClientsResponseData[]>([]);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [showStartCalendar, setShowStartCalendar] = useState(false);
    const [period, setPeriod] = useState(1);

    const [formData, setFormData] = useState<InternalFormData>({
        clientId: 0,
        period: 1,
        startedAt: new Date(),
        expireAt: new Date(new Date().setMonth(new Date().getMonth() + 1)),
        payday: new Date().getDate(),
        unitCost: 0,
        workerAmount: 0
    });

    useEffect(() => {
        if (!isOpen) return;

        const fetchClients = async () => {
            try {
                const data = await contractableClientsAPI();
                setClients(data);
                if (data.length > 0) {
                    setFormData(prev => ({ ...prev, clientId: data[0].clientId }));
                }
            } catch (error) {
                console.error('Error fetching clients:', error);
            }
        };

        fetchClients();
    }, [isOpen]);

    useEffect(() => {
        const expireAt = calculateEndDate(formData.startedAt, period);
        setFormData(prev => ({ ...prev, expireAt }));
    }, [formData.startedAt, period]);

    const formatDate = (date: Date): string => {
        const year = date.getFullYear();
        const month = `${date.getMonth() + 1}`.padStart(2, '0');
        const day = `${date.getDate()}`.padStart(2, '0');
        return `${year}-${month}-${day}`;
    };


    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setIsSubmitting(true);
        try {
            const payload: CreateContractRequest = {
                ...formData,
                startedAt: formatDate(formData.startedAt),
                expireAt: formatDate(formData.expireAt)
            };
            await createContractAPI(payload);
            onClose();
        } catch (error) {
            console.error('Error creating contract:', error);
        } finally {
            setIsSubmitting(false);
        }
    };

    const calculateEndDate = (startedAt: Date, months: number): Date => {
        let year = startedAt.getFullYear();
        let month = startedAt.getMonth() + months + 1;

        if (month > 12) {
            year += Math.floor((month - 1) / 12);
            month = month % 12;
            if (month === 0) month = 12;
        }

        const endDate = new Date(year, month - 1, startedAt.getDate() - 1);
        const lastDayOfMonth = new Date(year, month - 1, 0).getDate();
        if (endDate.getDate() > lastDayOfMonth) {
            endDate.setDate(lastDayOfMonth);
        }
        return endDate;
    };

    //클라이언트, 시작일, 계약일, 정기결제일, 작업자 단가 모두 입력한 경우 생성 가능
    const isSubmitDisabled = isSubmitting ||
        !formData.clientId ||
        formData.clientId === 0 ||
        !formData.startedAt ||
        !formData.expireAt ||
        !formData.payday ||
        formData.workerAmount <= 0 ||
        formData.unitCost <= 0;

    return (
        <Modal
            isOpen={isOpen}
            onRequestClose={onClose}
            className={styles.modal}
            overlayClassName={styles.overlay}
        >
            <div className={styles.container}>
                <div className={styles.header}>
                    <h2 className={styles.title}>계약 추가</h2>
                    {/*닫기 버튼*/}
                    <button
                        onClick={onClose}
                        className={styles.closeButton}
                        aria-label="Close"
                        disabled={isSubmitting}
                    >
                        ×
                    </button>
                </div>

                <form onSubmit={handleSubmit}>
                    <div className={styles.formGroup}>
                        <label>회사명</label>
                        {/*대상 클라이언트 선택*/}
                        <select
                            value={formData.clientId}
                            onChange={(e) => setFormData({...formData, clientId: Number(e.target.value)})}
                            className={styles.select}
                            required
                            disabled={isSubmitting}
                        >
                            {clients.map(client => (
                                <option key={client.clientId} value={client.clientId}>
                                    {client.companyName}
                                </option>
                            ))}
                        </select>
                    </div>

                    <div className={styles.formGroup}>
                        <label>계약기간</label>
                        <div className={styles.periodGroup}>
                            {/*계약 기간 선택*/}
                            <input
                                type="number"
                                value={period === 0 ? '' : period}
                                onChange={(e) => {
                                    setPeriod(Number(e.target.value));
                                    setFormData({...formData, period: Number(e.target.value)});
                                }}
                                className={styles.periodInput}
                                min="1"
                                disabled={isSubmitting}
                            />
                            <span className={styles.periodUnit}>개월</span>
                            <div className={styles.datePickerWrapper}>
                                {/*시작일, 만료일 출력*/}
                                <div
                                    className={styles.dateInput}
                                    onClick={() => setShowStartCalendar(!showStartCalendar)}
                                >
                                    {formData.startedAt.toLocaleDateString('ko-KR', {
                                        year: 'numeric',
                                        month: 'long',
                                        day: 'numeric'
                                    })}
                                    {' ~ '}
                                    {formData.expireAt.toLocaleDateString('ko-KR', {
                                        year: 'numeric',
                                        month: 'long',
                                        day: 'numeric'
                                    })}
                                </div>
                                {/*시작일 선택*/}
                                {showStartCalendar && (
                                    <div className={styles.calendarPopup}>
                                        <Calendar
                                            selectedDate={formData.startedAt}
                                            onDateSelect={(date) => {
                                                const expireAt = calculateEndDate(date, period);
                                                setFormData(prev => ({
                                                    ...prev,
                                                    startedAt: date,
                                                    expireAt: expireAt,
                                                    payday: date.getDate()
                                                }));
                                                setShowStartCalendar(false);
                                            }}
                                        />
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    <div className={styles.formGroup}>
                        <label>정산일</label>
                        {/*정기 결제일은 계약 시작일과 같은 일로 고정, 최대 28일*/}
                        <input
                            type="number"
                            value={formData.payday === 0 ? '' : formData.payday}
                            onChange={(e) => {
                                const value = Number(e.target.value);
                                if (value >= 1 && value <= 28) {
                                    setFormData(prev => ({
                                        ...prev,
                                        payday: value
                                    }));
                                }
                            }}
                            placeholder="계약 시작일"
                            className={styles.input}
                            required
                            min="1"
                            max="28"
                            disabled={true}
                        />
                        <span className={styles.periodUnit}>일</span>
                    </div>

                    <div className={styles.formGroup}>
                        <label>작업자 단가</label>
                        {/*작업자 단가 입력*/}
                        <input
                            type="number"
                            value={formData.unitCost === 0 ? '' : formData.unitCost}
                            onChange={(e) => {
                                const value = Number(e.target.value);
                                if (value >= 0) {  // 음수 방지
                                    setFormData(prev => ({
                                        ...prev,
                                        unitCost: value
                                    }));
                                }
                            }}
                            placeholder="단가 입력"
                            className={styles.input}
                            required
                            min="0"
                            disabled={isSubmitting}
                        />
                        <span className={styles.periodUnit}>원</span>
                    </div>

                    <div className={styles.formGroup}>
                        <label>작업자 수</label>
                        {/*작업자 수 입력*/}
                        <input
                            type="number"
                            value={formData.workerAmount === 0 ? '' : formData.workerAmount}
                            onChange={(e) => {
                                const value = Number(e.target.value);
                                if (value >= 0) {
                                    setFormData(prev => ({
                                        ...prev,
                                        workerAmount: value
                                    }));
                                }
                            }}
                            placeholder="작업자 수 입력"
                            className={styles.input}
                            required
                            min="0"
                            disabled={isSubmitting}
                        />
                        <span className={styles.periodUnit}>명</span>
                    </div>

                    {/*생성 버튼*/}
                    <button
                        type="submit"
                        className={styles.submitButton}
                        disabled={isSubmitDisabled}
                    >
                        {isSubmitting ? '처리 중...' : '추가'}
                    </button>
                </form>
            </div>
        </Modal>
    );
}

export default CreateContractPopUp;